import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ApproveEdpProposalData,
  CancelEdpMeetingData,
  ConfigureEdpCourseData,
  CreateEdpRoundData,
  EdpAfternoonParams,
  EdpAfternoonWeekParams,
  EdpBlock,
  EdpCourseGroupData,
  EdpCoursePayload,
  EdpDecisionData,
  EdpPaymentMethod,
  EdpPlacementDecision,
  EdpProposalFilters,
  EdpRegisterMarksData,
  SettleEdpCancellationData,
  SubmitEdpProposalsData,
  UpdateEdpRoundData,
  edpApi,
} from '@/services';

export const edpKeys = {
  all: ['edp'] as const,
  openRound: ['edp', 'rounds', 'open'] as const,
  rounds: ['edp', 'rounds'] as const,
  mine: ['edp', 'proposals', 'mine'] as const,
  queue: (filters: EdpProposalFilters) => ['edp', 'proposals', 'queue', filters] as const,
  detail: (id: string) => ['edp', 'proposals', 'detail', id] as const,
  course: (courseId: string) => ['edp', 'course', courseId] as const,
  demand: (roundId: string) => ['edp', 'demand', roundId] as const,
  coursesByTeacher: (roundId: string) => ['edp', 'courses-by-teacher', roundId] as const,
  schedule: (roundId: string) => ['edp', 'schedule', roundId] as const,
  students: (roundId: string) => ['edp', 'students', roundId] as const,
  adjustments: (roundId: string, studentId: string) => ['edp', 'adjustments', roundId, studentId] as const,
  payments: (roundId: string) => ['edp', 'payments', roundId] as const,
  paymentBalances: (roundId: string) => ['edp', 'payment-balances', roundId] as const,
  paymentTotals: (roundId: string) => ['edp', 'payment-totals', roundId] as const,
  applicants: (courseId: string) => ['edp', 'applicants', courseId] as const,
  afternoon: (params: EdpAfternoonParams) => ['edp', 'afternoon', params.date, params.scope, params.classId] as const,
  attendanceWeek: (date: string) => ['edp', 'attendance-week', date] as const,
  cancellations: (roundId: string) => ['edp', 'cancellations', roundId] as const,
  afternoonWeek: (params: EdpAfternoonWeekParams) =>
    ['edp', 'afternoon-week', params.date, params.scope, params.classId] as const,
  register: (courseId: string, date: string, block: EdpBlock | null) =>
    ['edp', 'register', courseId, date, block] as const,
  registerCourse: (courseId: string) => ['edp', 'register-course', courseId] as const,
  myCourses: (date: string) => ['edp', 'my-courses', date] as const,
  registerHistory: (courseId: string) => ['edp', 'register-history', courseId] as const,
  registerSummary: (courseId: string) => ['edp', 'register-summary', courseId] as const,
  registerLessons: (courseId: string) => ['edp', 'register-lessons', courseId] as const,
  roundRegisters: (roundId: string, from?: string, to?: string) =>
    ['edp', 'round-registers', roundId, from ?? null, to ?? null] as const,
};

export function useOpenEdpRoundQuery() {
  return useQuery({ queryKey: edpKeys.openRound, queryFn: () => edpApi.openRound() });
}

export function useEdpRoundsQuery(enabled = true) {
  return useQuery({ queryKey: edpKeys.rounds, queryFn: () => edpApi.listRounds(), enabled });
}

export function useCreateEdpRoundMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEdpRoundData) => edpApi.createRound(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

export function useUpdateEdpRoundMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEdpRoundData }) => edpApi.updateRound(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

export function useMyEdpProposalsQuery() {
  return useQuery({ queryKey: edpKeys.mine, queryFn: () => edpApi.listMine() });
}

export function useEdpProposalQueueQuery(filters: EdpProposalFilters = {}, enabled = true) {
  return useQuery({ queryKey: edpKeys.queue(filters), queryFn: () => edpApi.queue(filters), enabled });
}

export function useEdpProposalQuery(id: string, enabled = true) {
  return useQuery({ queryKey: edpKeys.detail(id), queryFn: () => edpApi.get(id), enabled });
}

export function useSubmitEdpProposalsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitEdpProposalsData) => edpApi.submit(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

export function useResubmitEdpProposalMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EdpCoursePayload) => edpApi.resubmit(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

export function useOfficeEditEdpProposalMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EdpCoursePayload) => edpApi.editByOffice(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

/** Adds another group to an approved proposal. Invalidates everything: a new course changes the
 *  queue, the proposal detail and the placement screens at once. */
export function useAddEdpGroupMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EdpCourseGroupData) => edpApi.addGroup(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

export function useWithdrawEdpProposalMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => edpApi.withdraw(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

export function useCommentEdpProposalMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => edpApi.comment(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

export function useApproveEdpProposalMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ApproveEdpProposalData) => edpApi.approve(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

/** One hook for the three note-only decisions; they differ only in endpoint. */
export function useEdpDecisionMutation(id: string, kind: 'requestChanges' | 'waitlist' | 'reject') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EdpDecisionData) => edpApi[kind](id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

/** §11b: price, publication and the guarded status move — see `EDP_COURSE_STATUS_TRANSITIONS`.
 *  Invalidates broadly: the course this changes is read back through a proposal's detail view,
 *  not through its own query key. */
export function useConfigureEdpCourseMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: string; data: ConfigureEdpCourseData }) =>
      edpApi.configureCourse(courseId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.all }),
  });
}

// ── Office registration & placement (subsystem 3) ───────────────────────────

/** `CoursePlacement`'s course-by-id fetch — the header, the price (previously write-only),
 *  and the same oversubscribed flag `demand` computes for the whole round. */
export function useEdpCourseQuery(courseId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.course(courseId),
    queryFn: () => edpApi.getCourse(courseId),
    enabled: enabled && !!courseId,
  });
}

export function useEdpDemandQuery(roundId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.demand(roundId),
    queryFn: () => edpApi.demand(roundId),
    enabled: enabled && !!roundId,
  });
}

export function useEdpScheduleQuery(roundId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.schedule(roundId),
    queryFn: () => edpApi.schedule(roundId),
    enabled: enabled && !!roundId,
  });
}

/** Every child in one round, with what they are doing and what they owe. */
export function useEdpAdjustmentsQuery(roundId: string, studentId: string | null) {
  return useQuery({
    queryKey: edpKeys.adjustments(roundId, studentId ?? ''),
    queryFn: () => edpApi.adjustments(roundId, studentId as string),
    enabled: !!roundId && !!studentId,
  });
}

/**
 * Both mutations invalidate the students list as well as the child's own adjustments: the list
 * carries every child's payable figure, and a discount that did not move it would leave the office
 * looking at the number they had just changed.
 */
export function useCreateEdpAdjustmentMutation(roundId: string, studentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { amount: number; reason: string }) => edpApi.createAdjustment(roundId, studentId, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: edpKeys.adjustments(roundId, studentId) }),
        queryClient.invalidateQueries({ queryKey: edpKeys.students(roundId) }),
      ]);
    },
  });
}

export function useRemoveEdpAdjustmentMutation(roundId: string, studentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => edpApi.removeAdjustment(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: edpKeys.adjustments(roundId, studentId) }),
        queryClient.invalidateQueries({ queryKey: edpKeys.students(roundId) }),
      ]);
    },
  });
}

export function useEdpStudentsQuery(roundId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.students(roundId),
    queryFn: () => edpApi.students(roundId),
    enabled: enabled && !!roundId,
  });
}

/** Every payment families have reported for one round, newest first. */
export function useEdpPaymentsQuery(roundId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.payments(roundId),
    queryFn: () => edpApi.payments(roundId),
    enabled: enabled && !!roundId,
  });
}

/** What each household owes against what has been confirmed. */
export function useEdpPaymentBalancesQuery(roundId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.paymentBalances(roundId),
    queryFn: () => edpApi.paymentBalances(roundId),
    enabled: enabled && !!roundId,
  });
}

/** What the round adds up to, for the cards above the queue. */
export function useEdpPaymentTotalsQuery(roundId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.paymentTotals(roundId),
    queryFn: () => edpApi.paymentTotals(roundId),
    enabled: enabled && !!roundId,
  });
}

/** The office's decision on one reported payment. Refreshes the balances and the per-child list too. */
export function useReviewEdpPaymentMutation(roundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: { confirm: boolean; note?: string } }) =>
      edpApi.reviewPayment(paymentId, data),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: edpKeys.payments(roundId) }),
        qc.invalidateQueries({ queryKey: edpKeys.paymentBalances(roundId) }),
        qc.invalidateQueries({ queryKey: edpKeys.paymentTotals(roundId) }),
        qc.invalidateQueries({ queryKey: edpKeys.students(roundId) }),
      ]);
    },
  });
}

/** A payment the office took itself. Lands confirmed, so the balances move immediately. */
export function useRecordEdpPaymentMutation(roundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      studentId: string;
      amount: number;
      note?: string;
      method?: EdpPaymentMethod;
      slip?: File | null;
    }) => edpApi.recordPayment(roundId, data),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: edpKeys.payments(roundId) }),
        qc.invalidateQueries({ queryKey: edpKeys.paymentBalances(roundId) }),
        qc.invalidateQueries({ queryKey: edpKeys.paymentTotals(roundId) }),
      ]);
    },
  });
}

/** Corrects a payment: a mistyped amount, or a note that turned out to be wrong. */
export function useUpdateEdpPaymentMutation(roundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentId,
      data,
    }: {
      paymentId: string;
      data: { amount?: number; note?: string; method?: EdpPaymentMethod };
    }) => edpApi.updatePayment(paymentId, data),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: edpKeys.payments(roundId) }),
        qc.invalidateQueries({ queryKey: edpKeys.paymentBalances(roundId) }),
        qc.invalidateQueries({ queryKey: edpKeys.paymentTotals(roundId) }),
      ]);
    },
  });
}

/** Removes a payment outright: a duplicate, or one entered against the wrong family. */
export function useDeleteEdpPaymentMutation(roundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) => edpApi.deletePayment(paymentId),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: edpKeys.payments(roundId) }),
        qc.invalidateQueries({ queryKey: edpKeys.paymentBalances(roundId) }),
        qc.invalidateQueries({ queryKey: edpKeys.paymentTotals(roundId) }),
      ]);
    },
  });
}

/** Issues a receipt for one confirmed payment. Only invalidates the payments list — a receipt
 *  changes nothing a balance or a total reads. */
export function useCreateEdpReceiptMutation(roundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) => edpApi.createReceipt(paymentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.payments(roundId) }),
  });
}

/** Issues a receipt for every confirmed payment in the round that does not already have one. */
export function useCreateAllEdpReceiptsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roundId: string) => edpApi.createAllReceipts(roundId),
    onSuccess: (_result, roundId) => qc.invalidateQueries({ queryKey: edpKeys.payments(roundId) }),
  });
}

/** Voids a wrongly-issued receipt, which frees its payment to be edited or deleted again — the
 *  row goes back to showing "Create receipt", same as the create mutations invalidate. */
export function useVoidEdpReceiptMutation(roundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ receiptId, reason }: { receiptId: string; reason: string }) => edpApi.voidReceipt(receiptId, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.payments(roundId) }),
  });
}

/** Re-renders a receipt's PDF after an earlier render failed. */
export function useRegenerateEdpReceiptMutation(roundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (receiptId: string) => edpApi.regenerateReceipt(receiptId),
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.payments(roundId) }),
  });
}

export function useSendEdpPaymentNoticesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roundId: string) => edpApi.sendPaymentNotices(roundId),
    // The round now knows it has been announced, which is what disables the button.
    onSuccess: () => qc.invalidateQueries({ queryKey: edpKeys.rounds }),
  });
}

export function useSendEdpPaymentRemindersMutation() {
  return useMutation({ mutationFn: (roundId: string) => edpApi.sendPaymentReminders(roundId) });
}

export function useEdpMyRunningQuery() {
  return useQuery({ queryKey: ['edp', 'my', 'running'], queryFn: () => edpApi.myRunning() });
}

export function useEdpApplicantsQuery(courseId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.applicants(courseId),
    queryFn: () => edpApi.applicants(courseId),
    enabled: enabled && !!courseId,
  });
}

/** Invalidates every round's demand (the flag a decision moves is round-scoped, not
 *  course-scoped), this course's own detail (its `confirmedCount`/`flag` move too), and this
 *  course's applicant list. */
function invalidatePlacement(qc: ReturnType<typeof useQueryClient>, courseId: string) {
  qc.invalidateQueries({ queryKey: ['edp', 'demand'] });
  qc.invalidateQueries({ queryKey: edpKeys.course(courseId) });
  qc.invalidateQueries({ queryKey: edpKeys.applicants(courseId) });
}

export function useEdpDecideMutation(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, decision }: { studentId: string; decision: EdpPlacementDecision }) =>
      edpApi.decide(courseId, studentId, decision),
    onSuccess: () => invalidatePlacement(qc, courseId),
  });
}

export function useEdpRemoveApplicantMutation(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (studentId: string) => edpApi.removeApplicant(courseId, studentId),
    onSuccess: () => invalidatePlacement(qc, courseId),
  });
}

/** Returns `{ confirmed, skipped }` — `skipped` is a child already holding this afternoon on
 *  another course. The caller must surface it: a bulk action producing fewer seats than
 *  applicants with no explanation reads as a bug. */
export function useEdpConfirmAllMutation(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => edpApi.confirmAll(courseId),
    onSuccess: () => invalidatePlacement(qc, courseId),
  });
}

export function useEdpManualEnrolMutation(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (studentId: string) => edpApi.enrolManually(courseId, studentId),
    onSuccess: () => invalidatePlacement(qc, courseId),
  });
}

/** Marks every settled, unnotified family in the round as told. Invalidates `demand` — a sent
 *  family stays counted the same way in the triage totals, but a second click must not be able
 *  to write to anyone the first one already reached. */
export function useSendEdpOutcomesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roundId: string) => edpApi.sendOutcomes(roundId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['edp', 'demand'] }),
  });
}

// ── The afternoon list ───────────────────────────────────────────────────────

/** Disabled until a class is actually chosen for the `'class'` scope — firing it without one
 *  would silently ask the API for something other than what the screen shows. */
export function useEdpAfternoonQuery(params: EdpAfternoonParams) {
  const enabled = params.scope !== 'class' || !!params.classId;
  return useQuery({
    queryKey: edpKeys.afternoon(params),
    queryFn: () => edpApi.afternoon(params),
    enabled,
  });
}

/** A class's week of EDP. Disabled until a class is chosen for the `'class'` scope, as the day is. */
export function useEdpAfternoonWeekQuery(params: EdpAfternoonWeekParams) {
  const enabled = params.scope !== 'class' || !!params.classId;
  return useQuery({
    queryKey: edpKeys.afternoonWeek(params),
    queryFn: () => edpApi.afternoonWeek(params),
    enabled,
  });
}

// ── Cancelled meetings ───────────────────────────────────────────────────────

export function useEdpCancellationsQuery(roundId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.cancellations(roundId),
    queryFn: () => edpApi.cancellations(roundId),
    enabled: enabled && !!roundId,
  });
}

/**
 * A struck-out afternoon changes what every EDP screen says about that day — the afternoon
 * lists, the registers, the week views, the schedule — so the whole EDP cache is refreshed rather
 * than a list of keys that would go stale the next time a screen was added.
 */
function useInvalidateEdp() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: edpKeys.all });
}

export function useCancelEdpMeetingMutation() {
  const invalidate = useInvalidateEdp();
  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: string; data: CancelEdpMeetingData }) =>
      edpApi.cancelMeeting(courseId, data),
    onSuccess: invalidate,
  });
}

export function useSettleEdpCancellationMutation() {
  const invalidate = useInvalidateEdp();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SettleEdpCancellationData }) => edpApi.settleCancellation(id, data),
    onSuccess: invalidate,
  });
}

export function useRemoveEdpCancellationMutation() {
  const invalidate = useInvalidateEdp();
  return useMutation({ mutationFn: (id: string) => edpApi.removeCancellation(id), onSuccess: invalidate });
}

/** The week's registers side by side, keyed on any date inside the week. */
export function useEdpAttendanceWeekQuery(date: string) {
  return useQuery({
    queryKey: edpKeys.attendanceWeek(date),
    queryFn: () => edpApi.attendanceWeek(date),
  });
}

// ── The register ─────────────────────────────────────────────────────────────

/** The course a register belongs to, readable by any member of staff. */
export function useEdpRegisterCourseQuery(courseId: string) {
  return useQuery({
    queryKey: edpKeys.registerCourse(courseId),
    queryFn: () => edpApi.registerCourse(courseId),
    enabled: !!courseId,
  });
}

/**
 * One meeting's register. Disabled until a block is settled: a course meeting twice on a day
 * has no single register, and asking for one before the user has chosen would show marks from
 * the wrong half of the afternoon.
 */
export function useEdpRegisterQuery(courseId: string, date: string, block: EdpBlock | null) {
  return useQuery({
    queryKey: edpKeys.register(courseId, date, block),
    queryFn: () => edpApi.register(courseId, date, block as EdpBlock),
    enabled: !!courseId && !!date && !!block,
    // The API's refusals here are answers, not blips — "the course does not meet then", "the
    // school was closed that day" — and retrying one three times only delays showing it.
    retry: false,
  });
}

/** Who runs what this round — the office's view once submission has closed. */
export function useEdpCoursesByTeacherQuery(roundId: string | undefined) {
  return useQuery({
    queryKey: edpKeys.coursesByTeacher(roundId ?? ''),
    queryFn: () => edpApi.coursesByTeacher(roundId as string),
    enabled: !!roundId,
  });
}

/** The caller's own EDP courses for a day, for "Classes today". */
export function useEdpMyCoursesQuery(date: string) {
  return useQuery({
    queryKey: edpKeys.myCourses(date),
    queryFn: () => edpApi.myCourses(date),
    enabled: !!date,
  });
}

/** Every mark the course has ever carried, whichever days it meets on now. */
export function useEdpRegisterHistoryQuery(courseId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.registerHistory(courseId),
    queryFn: () => edpApi.registerHistory(courseId),
    enabled: enabled && !!courseId,
    // As on the register beside it: a refusal here is an answer, and the caller has to be able
    // to show it rather than let an empty list stand in for one.
    retry: false,
  });
}

/**
 * What the course's register adds up to so far: the counts, and how much of it is unfilled.
 *
 * `retry: false` as on the two beside it — a refusal here is an answer, and an unmarked register
 * must never be shown in place of one the screen simply could not load.
 */
export function useEdpRegisterSummaryQuery(courseId: string, enabled = true) {
  return useQuery({
    queryKey: edpKeys.registerSummary(courseId),
    queryFn: () => edpApi.registerSummary(courseId),
    enabled: enabled && !!courseId,
    retry: false,
  });
}

/** Every lesson of the course across its round, with how far each register has got. */
export function useEdpRegisterLessonsQuery(courseId: string) {
  return useQuery({
    queryKey: edpKeys.registerLessons(courseId),
    queryFn: () => edpApi.registerLessons(courseId),
    enabled: !!courseId,
  });
}

/** Every lesson of the round between two dates, with who runs each course. The office's view. */
export function useEdpRoundRegistersQuery(roundId: string, range: { from?: string; to?: string }) {
  return useQuery({
    queryKey: edpKeys.roundRegisters(roundId, range.from, range.to),
    queryFn: () => edpApi.roundRegisters(roundId, range),
    enabled: !!roundId,
  });
}

/** Saves a whole register at once. Invalidates the history, the summary and the lesson list — a save changes all three. */
export function useMarkEdpRegisterMutation(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EdpRegisterMarksData) => edpApi.markRegister(courseId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edp', 'register', courseId] });
      qc.invalidateQueries({ queryKey: edpKeys.registerHistory(courseId) });
      qc.invalidateQueries({ queryKey: edpKeys.registerSummary(courseId) });
      qc.invalidateQueries({ queryKey: edpKeys.registerLessons(courseId) });
    },
  });
}
